import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ScrollTop from "components/ScrollTop";
import { RouteDocumentTitle } from "components/DocumentTitle";
import KotharProvider from "context/provider";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Routes from "routes";
import useInactivityTimer from "./hooks/useInactivityTimer";
import axios from "const/axios";
import { useEffect } from "react";
import { ORG_DELETED_ERROR_CODE } from "utils/organizationAccess";
import { initNativeShell } from "utils/nativeBridge";

const isServerOrNetworkError = (error) => error?.response?.status >= 500 || !error?.response;

const hasApiErrorMessage = (error) =>
  Boolean(error?.response?.data?.errorMessage || error?.response?.data?.message);

/** Auth endpoints return caller-specific toasts; skip global server-unavailable noise. */
const isAuthRequest = (error) => {
  const url = error?.config?.url || "";
  return /\/auth\/|\/forgot-password|\/reset-password|\/change-password/.test(url);
};

const shouldShowServerUnavailableToast = (error) =>
  !isAuthRequest(error) && isServerOrNetworkError(error) && !hasApiErrorMessage(error);

// Inner component that uses the inactivity timer hook
// Must be inside QueryClientProvider to access useQueryClient
const AppContent = () => {
  // Initialize inactivity timer with 30 minutes timeout
  useInactivityTimer(30);
  useEffect(() => {
    let dispose;
    initNativeShell().then((fn) => {
      dispose = fn;
    });
    return () => dispose?.();
  }, []);

  return (
    <KotharProvider>
      <ToastContainer position="bottom-center" autoClose={2000} />
      <ScrollTop>
        <RouteDocumentTitle />
        <Routes />
      </ScrollTop>
    </KotharProvider>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      retry: (failureCount, error) => {
        // Don't retry on network errors or server errors
        if (error?.response?.status >= 500 || !error?.response) {
          return false;
        }
        return failureCount === 1;
      },
      onError: (error) => {
        // Handle server/backend errors
        console.log(error);

        // if (error?.response?.status >= 500 || !error?.response) {
        //   toast.error(
        //     "Server is currently unavailable. Please try again later."
        //   );
        //   // Clear any stored tokens
        //   localStorage.removeItem("token");
        //   // Navigate to login page with delay to show toast
        //   setTimeout(() => {
        //     window.location.href = "/login";
        //   }, 2000);
        // }
      },
    },
    mutations: {
      onError: (error) => {
        // Log mutation errors for debugging
        // Note: We don't redirect to login here as server errors (500+) or network errors
        // should be handled gracefully without logging users out. Only authentication
        // errors (401/403) should trigger logout, and those should be handled in
        // individual components or a dedicated auth error handler.
        console.log("mutation error", error);

        // Show user-friendly error message for server/network errors
        if (shouldShowServerUnavailableToast(error)) {
          toast.error("Server is currently unavailable. Please try again later.");
        }
      },
    },
  },
});

const App = () => {
  // Add axios interceptors for global error handling
  useEffect(() => {
    // Request interceptor
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const errorCode = error?.response?.data?.errorCode;
        if (errorCode === ORG_DELETED_ERROR_CODE) {
          try {
            const raw = localStorage.getItem("userDetail");
            if (raw) {
              const user = JSON.parse(raw);
              user.organizationDeleted = true;
              localStorage.setItem("userDetail", JSON.stringify(user));
            }
          } catch {
            // ignore parse errors
          }
          if (window.location.pathname !== "/organization-deleted") {
            window.location.href = "/organization-deleted";
          }
          return Promise.reject(error);
        }

        // Handle server/backend errors gracefully
        // Note: We removed the automatic redirect to login as it was too aggressive.
        // Server errors (500+) or network errors should not automatically log users out.
        // Only authentication errors (401/403) should trigger logout, and those should
        // be handled in individual components or a dedicated auth error handler.
        if (shouldShowServerUnavailableToast(error)) {
          toast.error("Server is currently unavailable. Please try again later.");
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};
export default App;
